import type { ThreadAsset, ThreadCluster, ThreadColumn, ThreadRow } from "../thread-design-system";

function rowIds(rows: readonly ThreadRow[], start = 0, count = 6): string[] {
  return rows.slice(start, start + count).map((row) => row.runId);
}

function cellRefs(rows: readonly ThreadRow[], col: string, start = 0, count = 4) {
  return rows.slice(start, start + count).map((row) => ({ runId: row.runId, col }));
}

function firstQuantity(columns: readonly ThreadColumn[], fallback = "cl"): string {
  return columns.find((column) => column.kind === "quantity")?.id ?? fallback;
}

function hasType(assets: readonly ThreadAsset[], type: ThreadAsset["type"]): boolean {
  return assets.some((asset) => asset.type === type);
}

function hasSource(assets: readonly ThreadAsset[], source: ThreadAsset["source"]): boolean {
  return assets.some((asset) => asset.source === source);
}

function richAssetTemplates(
  rows: readonly ThreadRow[],
  columns: readonly ThreadColumn[],
  existingAssets: readonly ThreadAsset[],
): ThreadAsset[] {
  const quantity = firstQuantity(columns);
  const firstRows = rowIds(rows, 0, 6);
  const midRows = rowIds(rows, Math.max(0, Math.floor(rows.length / 3)), 6);
  const lateRows = rowIds(rows, Math.max(0, rows.length - 6), 6);
  const firstCells = cellRefs(rows, quantity, 0, 5);
  const midCells = cellRefs(rows, quantity, Math.max(0, Math.floor(rows.length / 3)), 4);

  return [
    {
      id: "rich-ppt-sharepoint-review",
      type: "deck",
      source: "powerpoint",
      label: "Q-review evidence deck",
      sub: "PowerPoint · SharePoint · slide 14",
      external: "sharepoint://aero/reviews/q-review-evidence.pptx#slide=14",
      updated: "2 h ago",
      health: hasSource(existingAssets, "powerpoint") ? "fresh" : "warn",
      owner: "program-office",
      version: "draft 6",
      perms: "restricted",
      summary: "Slide 14 uses the selected coefficient sweep and is marked stale if upstream run or plot lineage changes.",
      preview: { kind: "deck", slideTitle: "Coefficient evidence pack", slideIndex: 14 },
      refs: { rows: firstRows, cells: firstCells, artifacts: ["rich-xlsx-sizing-book"], upstream: ["presentation", "deck", "plot", "table"] },
    },
    {
      id: "rich-google-slides-polar",
      type: "deck",
      source: "google-slides",
      label: "Polar readout for design review",
      sub: "Google Slides · linked image replacements",
      external: "docs.google.com/presentation/d/thread-v3-demo/edit#slide=id.p7",
      updated: "38 m ago",
      health: "fresh",
      owner: "a.kim",
      version: "v3",
      perms: "team",
      summary: "Managed slide images point back to the table rows, plot assets, and this Thread view.",
      preview: { kind: "deck", slideTitle: "Polar readout", slideIndex: 7 },
      refs: { rows: midRows, cells: midCells, upstream: ["presentation", "deck", "plot", "table"] },
    },
    {
      id: "rich-xlsx-sizing-book",
      type: "spreadsheet",
      source: "excel",
      label: "Sizing workbook · aero deltas",
      sub: "Excel on SharePoint · Sheet Aero!B12:H38",
      external: "sharepoint://aero/workbooks/wb-v7-sizing.xlsx#Aero!B12:H38",
      updated: "51 m ago",
      health: "warn",
      owner: "j.morales",
      version: "v18",
      perms: "restricted",
      summary: "Workbook cells consume CL/CD deltas and feed the Q-review deck. Thread can show which rows changed underneath the pasted values.",
      preview: { kind: "spreadsheet", workbook: "wb-v7-sizing.xlsx", sheet: "Aero", range: "B12:H38", rowCount: firstRows.length },
      refs: { rows: firstRows, cells: firstCells, upstream: ["spreadsheet", "table", "plot"], downstream: ["rich-ppt-sharepoint-review"] },
    },
    {
      id: "rich-google-sheets-test-matrix",
      type: "spreadsheet",
      source: "google-sheets",
      label: "Test matrix tracker",
      sub: "Google Sheets · Runs!A4:N62",
      external: "docs.google.com/spreadsheets/d/thread-v3-demo/edit#gid=42",
      updated: "1 d ago",
      health: "stale",
      owner: "test-team",
      version: "v11",
      perms: "team",
      summary: "Program tracker still points at earlier run IDs; selected Thread slice shows which rows should replace them.",
      preview: { kind: "spreadsheet", workbook: "WB v7 test matrix", sheet: "Runs", range: "A4:N62", rowCount: lateRows.length },
      refs: { rows: lateRows, cells: cellRefs(rows, quantity, Math.max(0, rows.length - 6), 4), upstream: ["spreadsheet", "table"] },
    },
    {
      id: "rich-slack-pinched-cl",
      type: "chat",
      source: "slack",
      label: "CL pinch above 4 deg",
      sub: "#aero-wing-body · 27 msgs · smart summary",
      external: "slack://C03A1QXYZ/p1714680000",
      updated: "31 m ago",
      health: "fresh",
      owner: "r.liu, a.kim",
      perms: "team",
      summary: "Team debated whether the high-alpha CL pinch is mesh, model-family, or real physics. Action: compare the selected slice against LES before the deck refresh.",
      questions: ["Is the pinch grid-converged?", "Which model family is safe to compare here?"],
      preview: { kind: "conversation", channel: "#aero-wing-body", participants: ["r.liu", "a.kim", "j.morales"], lastMsg: "Run the slice against LES before review." },
      refs: { rows: midRows, cells: midCells, artifacts: ["rich-ppt-sharepoint-review"], upstream: ["conversation", "slack", "mesh", "run"] },
    },
    {
      id: "rich-email-program-evidence",
      type: "email",
      source: "email",
      label: "FW: evidence pack for Friday",
      sub: "Outlook thread · 9 msgs · smart summary",
      external: "outlook://thread-v3-demo/evidence-pack",
      updated: "4 h ago",
      health: "warn",
      owner: "program-office",
      perms: "restricted",
      summary: "Program office asked whether the deck, workbook, and latest table all cite the same coefficient source rows.",
      questions: ["Re-issue the Google Slides deck?", "Mark workbook values stale?"],
      preview: { kind: "conversation", channel: "Outlook", participants: ["program-office", "a.kim", "j.morales"], lastMsg: "Please confirm the workbook and deck are using the same data." },
      refs: { rows: firstRows, cells: firstCells, artifacts: ["rich-ppt-sharepoint-review", "rich-xlsx-sizing-book"], upstream: ["conversation", "email", "presentation", "spreadsheet"] },
    },
    {
      id: "rich-uvf-flowfield",
      type: "uvf",
      source: "uvf",
      label: "Flowfield slice at selected condition",
      sub: "UVF · pressure/Cp view",
      external: "thread://assets/uvf/flowfield-slice",
      updated: "12 m ago",
      health: "fresh",
      owner: "k.adams",
      version: "v4",
      perms: "team",
      summary: "Instantiable UVF asset tied to the same row/cell slice as the table and deck evidence.",
      preview: { kind: "uvf", view: "slice" },
      refs: { rows: midRows, cells: midCells, upstream: ["uvf", "mesh", "run"] },
    },
    {
      id: "rich-decision-record",
      type: "decision",
      source: "thread",
      label: "Decision · rerun fine mesh",
      sub: "Decision record · accepted",
      external: "thread://decisions/rerun-fine-mesh",
      updated: "18 m ago",
      health: "fresh",
      owner: "aero-lead",
      perms: "team",
      summary: "Decision links Slack, email, workbook, and selected table slice: rerun fine mesh before the customer evidence pack is refreshed.",
      preview: { kind: "report", title: "Rerun fine mesh decision", abstract: "Accepted action with linked evidence and stale downstream assets." },
      refs: { rows: midRows, cells: midCells, artifacts: ["rich-slack-pinched-cl", "rich-email-program-evidence"], upstream: ["decision", "conversation", "run"] },
    },
    {
      id: "rich-runway-storyboard",
      type: "storyboard",
      source: "runway",
      label: "Runway storyboard · lineage reveal",
      sub: "16:9 video prompt · 8 shots",
      external: "runway://thread-v3/lineage-reveal",
      updated: "today",
      health: "fresh",
      owner: "h.smith",
      perms: "team",
      summary: "Video storyboard turns the selected table slice into a DAG-to-deck provenance reveal.",
      preview: { kind: "storyboard", title: "Lineage reveal", shotCount: 8, aspectRatio: "16:9" },
      refs: { rows: firstRows, cells: firstCells, artifacts: ["rich-ppt-sharepoint-review", "rich-google-slides-polar"], upstream: ["storyboard", "video", "presentation"] },
    },
  ];
}

export function enrichV3Assets(
  assets: readonly ThreadAsset[],
  rows: readonly ThreadRow[],
  columns: readonly ThreadColumn[],
): ThreadAsset[] {
  if (!rows.length) return [...assets];
  const templates = richAssetTemplates(rows, columns, assets);
  const required = templates.filter((asset) => {
    if (asset.type === "spreadsheet") return !hasType(assets, "spreadsheet");
    if (asset.source === "excel" || asset.source === "google-sheets") return !hasSource(assets, asset.source);
    if (asset.source === "powerpoint" || asset.source === "google-slides") return !hasSource(assets, asset.source);
    if (asset.source === "slack" || asset.source === "email") return !hasSource(assets, asset.source);
    if (asset.type === "uvf" || asset.type === "decision" || asset.type === "storyboard") return !hasType(assets, asset.type);
    return false;
  });

  const byId = new Map<string, ThreadAsset>();
  for (const asset of assets) byId.set(asset.id, asset);
  for (const asset of required) byId.set(asset.id, asset);
  return [...byId.values()];
}

export function enrichV3Clusters(clusters: readonly ThreadCluster[], assets: readonly ThreadAsset[]): ThreadCluster[] {
  const byId = new Map<string, ThreadCluster>(clusters.map((cluster) => [cluster.id, cluster]));
  const specs: ThreadCluster[] = [
    { id: "table", type: "table", label: "Tables", nodes: assets.filter((asset) => asset.type === "table-view" || asset.refs.upstream?.includes("table")).length },
    { id: "plot", type: "plot", label: "Plots", nodes: assets.filter((asset) => asset.type === "plot" || asset.refs.upstream?.includes("plot")).length },
    { id: "run", type: "run", label: "Runs", nodes: assets.filter((asset) => asset.refs.upstream?.includes("run")).length },
    { id: "mesh", type: "mesh", label: "Meshes", nodes: assets.filter((asset) => asset.refs.upstream?.includes("mesh")).length },
    { id: "presentation", type: "presentation", label: "Presentations", nodes: assets.filter((asset) => asset.type === "deck" || asset.type === "slide-deck").length },
    { id: "spreadsheet", type: "spreadsheet", label: "Spreadsheets", nodes: assets.filter((asset) => asset.type === "spreadsheet").length },
    { id: "conversation", type: "conversation", label: "Conversations", nodes: assets.filter((asset) => asset.type === "chat" || asset.type === "email").length },
    { id: "decision", type: "decision", label: "Decisions", nodes: assets.filter((asset) => asset.type === "decision").length },
    { id: "uvf", type: "uvf", label: "UVF Views", nodes: assets.filter((asset) => asset.type === "uvf").length },
    { id: "storyboard", type: "storyboard", label: "Storyboards", nodes: assets.filter((asset) => asset.type === "storyboard" || asset.type === "runway-video").length },
  ];

  for (const cluster of specs) {
    if (cluster.nodes > 0 && !byId.has(cluster.id)) byId.set(cluster.id, cluster);
  }
  return [...byId.values()];
}

export function enrichV3GraphEdges(
  edges: readonly (readonly [string, string])[],
  clusters: readonly ThreadCluster[],
): Array<readonly [string, string]> {
  const clusterIds = new Set(clusters.map((cluster) => cluster.id));
  const chain: Array<readonly [string, string]> = [
    ["table", "spreadsheet"],
    ["plot", "presentation"],
    ["spreadsheet", "presentation"],
    ["presentation", "conversation"],
    ["conversation", "decision"],
    ["run", "uvf"],
    ["presentation", "storyboard"],
  ];
  const deduped = new Set(edges.map(([source, target]) => `${source}|${target}`));
  for (const [source, target] of chain) {
    if (clusterIds.has(source) && clusterIds.has(target)) deduped.add(`${source}|${target}`);
  }
  return [...deduped].map((edge) => edge.split("|") as [string, string]);
}
